import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const StoreContext = createContext(null);
const CART_KEY = "ismotech_cart";
const FAV_KEY = "ismotech_favorites";
const CURRENCY_KEY = "ismotech_currency";

const read = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    return fallback;
  }
};

export function StoreProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(() => read(CART_KEY, []));
  const [favorites, setFavorites] = useState(() => read(FAV_KEY, []));
  const [currency, setCurrency] = useState(() => localStorage.getItem(CURRENCY_KEY) || "XOF");

  useEffect(() => localStorage.setItem(CART_KEY, JSON.stringify(items)), [items]);
  useEffect(() => localStorage.setItem(FAV_KEY, JSON.stringify(favorites)), [favorites]);
  useEffect(() => localStorage.setItem(CURRENCY_KEY, currency), [currency]);

  const addToCart = useCallback((product, quantity = 1, fields = null) => {
    setItems((prev) => {
      const found = prev.find((i) => i.product_id === product.id);
      if (found) {
        return prev.map((i) =>
          i.product_id === product.id
            ? { ...i, quantity: Math.min(i.quantity + quantity, 20), fields: fields || i.fields || {} }
            : i,
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          quantity,
          fields: fields || {},
          name: product.name,
          slug: product.slug,
          price: product.price,
          old_price: product.old_price,
          image_url: product.image_url,
          license_type: product.license_type,
          duration: product.duration,
        },
      ];
    });
    toast.success("Ajouté au panier", { description: product.name });
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    setItems((prev) =>
      quantity <= 0
        ? prev.filter((i) => i.product_id !== productId)
        : prev.map((i) => (i.product_id === productId ? { ...i, quantity } : i)),
    );
  }, []);

  const removeFromCart = useCallback((productId) => {
    setItems((prev) => prev.filter((i) => i.product_id !== productId));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const toggleFavorite = useCallback(
    async (product) => {
      const exists = favorites.some((f) => f.product_id === product.id);
      setFavorites((prev) =>
        exists
          ? prev.filter((f) => f.product_id !== product.id)
          : [...prev, { product_id: product.id, name: product.name, slug: product.slug }],
      );
      toast[exists ? "message" : "success"](exists ? "Retiré des favoris" : "Ajouté aux favoris", {
        description: product.name,
      });
      if (user) {
        try {
          if (exists) await api.delete(`/my/favorites/${product.id}`);
          else await api.post(`/my/favorites/${product.id}`);
        } catch (e) {
          /* silencieux */
        }
      }
    },
    [favorites, user],
  );

  const isFavorite = useCallback((productId) => favorites.some((f) => f.product_id === productId), [favorites]);

  const cartCount = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);
  const subtotal = useMemo(() => items.reduce((sum, i) => sum + i.price * i.quantity, 0), [items]);
  const savings = useMemo(
    () => items.reduce((sum, i) => sum + Math.max((i.old_price || 0) - i.price, 0) * i.quantity, 0),
    [items],
  );

  const value = useMemo(
    () => ({
      items,
      cartCount,
      subtotal,
      savings,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      favorites,
      toggleFavorite,
      isFavorite,
      currency,
      setCurrency,
    }),
    [items, cartCount, subtotal, savings, addToCart, updateQuantity, removeFromCart, clearCart, favorites,
      toggleFavorite, isFavorite, currency],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export const useStore = () => useContext(StoreContext);
